import { get } from 'svelte/store';

import type { PaymentDetails, UserCurrent } from '@api/types';
import { LL } from '@i18n/i18n-svelte';
import { EMAIL_LENGTH_MAX, EMAIL_LENGTH_MIN, EMAIL_REGEX, LATIN_REGEX } from '@shared/constants';
import { EU_COUNTRIES } from '@shared/countries';
import {
    BITCOIN_LENGTH_MAX,
    BITCOIN_LENGTH_MIN,
    IBAN_REGEX,
    SAN_LENGTH_MAX,
    SAN_LENGTH_MIN,
    SAN_REGEXP,
    SWIFT_LENGTH_OPTIONS,
    SWIFT_REGEX,
    USDT_LENGTH,
} from '@shared/operator/constants';
import { PayoutMethod } from '@shared/types';

import type { PaymentDetailsValidationResult } from './types';

const euCountries = new Set<string>(EU_COUNTRIES);
export function checkEuCountry(country?: string): boolean {
    return country ? euCountries.has(country) : false;
}

export function validate(
    data: PaymentDetails,
    isAgreementAccepted: boolean
): PaymentDetailsValidationResult {
    return {
        ...validateTaxInputGroup(data.taxInfo),
        ...validatePaymentInputGroup(data),
        ...validateBankInputGroup(data.bankInfo, data.payoutMethod),
        ...(!isAgreementAccepted ? { ['isAgreementAccepted']: 'nothing' } : {}),
    };
}

function validateTaxInputGroup(data: PaymentDetails['taxInfo']): PaymentDetailsValidationResult {
    let result: PaymentDetailsValidationResult = {};
    const $LL = get(LL);
    if (!data.country) {
        return { ...result, ['taxInfo.country']: $LL.profile.paymentDetails.countryError() };
    }

    if (!checkEuCountry(data.country)) {
        return result;
    }

    if (!data.city) {
        result = { ...result, ['taxInfo.city']: $LL.profile.paymentDetails.cityError() };
    } else if (!LATIN_REGEX.test(data.city)) {
        result = { ...result, ['taxInfo.city']: $LL.validationErrors.nonLatin() };
    }

    if (data.address && !LATIN_REGEX.test(data.address)) {
        result = { ...result, ['taxInfo.address']: $LL.validationErrors.nonLatin() };
    }

    if (data.noTin) {
        if (!data.birthPlace) {
            result = { ...result, ['taxInfo.birthPlace']: $LL.profile.paymentDetails.birthPlaceError() };
        } else if (!LATIN_REGEX.test(data.birthPlace)) {
            result = { ...result, ['taxInfo.birthPlace']: $LL.validationErrors.nonLatin() };
        }
    }

    if (!data.noTin && !data.tin) {
        result = { ...result, ['taxInfo.tin']: $LL.profile.paymentDetails.tinError() };
    }

    return result;
}

function validatePaymentInputGroup(data: PaymentDetails): PaymentDetailsValidationResult {
    let result: PaymentDetailsValidationResult = {};
    let error: string | undefined;

    const $LL = get(LL);
    switch (data.payoutMethod) {
        case PayoutMethod.Bitcoin:
            if (
                data.payoutDetails.length < BITCOIN_LENGTH_MIN
                || data.payoutDetails.length > BITCOIN_LENGTH_MAX
                || !(data.payoutDetails.startsWith('1')
                    || data.payoutDetails.startsWith('3')
                    || data.payoutDetails.startsWith('bc1')
                )
            ) {
                result = {
                    ...result,
                    ['payoutDetails']: $LL.profile.paymentDetails.bitcoinError({
                        minLength: BITCOIN_LENGTH_MIN,
                        maxLength: BITCOIN_LENGTH_MAX,
                    }),
                };
            }
            break;
        case PayoutMethod.Paxum:
            if (
                data.payoutDetails.length < EMAIL_LENGTH_MIN
                || data.payoutDetails.length > EMAIL_LENGTH_MAX
                || !EMAIL_REGEX.test(data.payoutDetails)
            ) {
                result = { ...result, ['payoutDetails']: $LL.validationErrors.email.format() };
            }
            break;
        case PayoutMethod.Usdt:
            if (!data.payoutDetails.startsWith('T') || data.payoutDetails.length !== USDT_LENGTH) {
                result = {
                    ...result,
                    ['payoutDetails']: $LL.profile.paymentDetails.usdtError({ length: USDT_LENGTH }),
                };
            }
            break;
        case PayoutMethod.Cosmo:
            error = validateSAN(data.payoutInfo.san);
            if (error) {
                result = { ...result, ['payoutInfo.san']: error };
            }
            break;
        case PayoutMethod.Sepa:
            if (!data.payoutInfo.street) {
                result = { ...result, ['payoutInfo.street']: $LL.validationErrors.required() };
            } else if (!LATIN_REGEX.test(data.payoutInfo.street)) {
                result = { ...result, ['payoutInfo.street']: $LL.validationErrors.nonLatin() };
            }

            if (!data.payoutInfo.city) {
                result = { ...result, ['payoutInfo.city']: $LL.validationErrors.required() };
            } else if (!LATIN_REGEX.test(data.payoutInfo.city)) {
                result = { ...result, ['payoutInfo.city']: $LL.validationErrors.nonLatin() };
            }

            if (!data.payoutInfo.zip) {
                result = { ...result, ['payoutInfo.zip']: $LL.validationErrors.required() };
            } else if (!LATIN_REGEX.test(data.payoutInfo.zip)) {
                result = { ...result, ['payoutInfo.zip']: $LL.validationErrors.nonLatin() };
            }
            break;
        default:
            result = { ...result, ['payoutMethod']: $LL.profile.paymentDetails.paymentMethodError() };
    }
    return result;
}

function validateSAN(value: string | undefined): string | undefined {
    const $LL = get(LL);

    if (!value) return $LL.validationErrors.required();

    if (value.length < SAN_LENGTH_MIN || value.length > SAN_LENGTH_MAX || !SAN_REGEXP.test(value)) {
        return $LL.profile.paymentDetails.sanError();
    }
}

function validateBankInputGroup(
    data: PaymentDetails['bankInfo'],
    payoutMethod: PaymentDetails['payoutMethod']
) : PaymentDetailsValidationResult {
    const result: PaymentDetailsValidationResult = {};
    const $LL = get(LL);

    if (!data || payoutMethod !== PayoutMethod.Sepa) return result;

    if (!data.iban) {
        return { ...result, ['bankInfo.iban']: $LL.validationErrors.required() };
    } else if (!IBAN_REGEX.test(data.iban)) {
        return { ...result, ['bankInfo.iban']: $LL.profile.paymentDetails.bankDetails.ibanPatterError() };
    }

    if (!data.country) {
        return { ...result, ['bankInfo.country']: $LL.validationErrors.required() };
    }

    if (!data.name) {
        return { ...result, ['bankInfo.name']: $LL.validationErrors.required() };
    } else if (!LATIN_REGEX.test(data.name)) {
        return { ...result, ['bankInfo.name']: $LL.validationErrors.nonLatin() };
    }

    if (!data.swift) {
        return { ...result, ['bankInfo.swift']: $LL.profile.paymentDetails.bankDetails.swiftRequiredError() };
    } else if (!SWIFT_LENGTH_OPTIONS.includes(data.swift.length)) {
        return { ...result, ['bankInfo.swift']: $LL.profile.paymentDetails.bankDetails.swiftLengthError() };
    } else if (!SWIFT_REGEX.test(data.swift)) {
        return { ...result, ['bankInfo.swift']: $LL.profile.paymentDetails.bankDetails.swiftPatterError() };
    }

    return result;
}

export function isUpdateTaxAlertNeeded(user: UserCurrent | undefined): boolean {
    const isTaxInfoFilled = Boolean(user?.operator?.taxInfoUpdatedAt);
    const isPayoutDetailsFilled = Boolean(user?.operator?.payoutDetailsUpdatedAt);
    return isPayoutDetailsFilled && !isTaxInfoFilled;
}
